import { useEffect, useRef, useState } from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { ThumbsUp, ThumbsDown } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  MESSAGE_FEEDBACK_REASON_CATALOG,
  MESSAGE_FEEDBACK_COMMENT_MAX,
  type MessageFeedbackRating,
  type MessageFeedbackReason,
  type MessageFeedbackState,
} from '@hector/shared-types';
import { useMessageFeedback } from '../../hooks/useMessageFeedback';

interface MessageFeedbackProps {
  /** Real `casediscovery_messages_v2.id` of the assistant turn being rated. */
  messageId: string;
  /** Vote already cast by the user (loaded on conversation open) — re-lights the thumb. */
  initial?: MessageFeedbackState;
}

/**
 * Thumbs up/down for an assistant message. The vote is persisted on click and a
 * toast confirms it immediately — so the user never doubts it was sent. On a
 * thumbs-down, a popover (anchored to the thumb, floating over the content so it
 * never shifts the layout) offers an OPTIONAL detail: a reason chip + free-text
 * comment. Dismissing the popover keeps the vote; "Envoyer" only adds the detail.
 *
 * v1 keeps the vote state local (optimistic, write-only) — existing votes are not
 * re-loaded when scrolling back. Reverts on API failure.
 */
export function MessageFeedback({ messageId, initial }: MessageFeedbackProps) {
  const { submit, remove, pending } = useMessageFeedback(messageId);
  const [rating, setRating] = useState<MessageFeedbackRating | null>(initial?.rating ?? null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [reason, setReason] = useState<MessageFeedbackReason | null>(initial?.reason ?? null);
  const [comment, setComment] = useState(initial?.comment ?? '');

  // The loaded feedback map can arrive AFTER this component mounts (async GET).
  // Sync from `initial` once it lands — but never clobber a vote the user has
  // already changed this session (touchedRef guards that).
  const touchedRef = useRef(false);
  useEffect(() => {
    if (touchedRef.current || !initial) return;
    setRating(initial.rating);
    setReason(initial.reason ?? null);
    setComment(initial.comment ?? '');
  }, [initial]);

  const handleUp = async () => {
    if (pending) return;
    touchedRef.current = true;
    if (rating === 'up') {
      setRating(null);
      const ok = await remove();
      if (!ok) setRating('up');
      return;
    }
    setRating('up');
    setDetailsOpen(false);
    const ok = await submit({ rating: 'up' });
    if (ok) toast('Merci, votre retour est enregistré');
    else setRating(null);
  };

  const handleDown = async () => {
    if (pending) return;
    touchedRef.current = true;
    if (rating === 'down') {
      // Toggle off the existing down-vote.
      setRating(null);
      setDetailsOpen(false);
      const ok = await remove();
      if (!ok) setRating('down');
      return;
    }
    // Record the down-vote immediately + confirm, then open the (optional) detail popover.
    setRating('down');
    const ok = await submit({ rating: 'down' });
    if (ok) {
      toast('Merci, votre retour est enregistré');
      setDetailsOpen(true);
    } else {
      setRating(null);
    }
  };

  const handleSendDetails = async () => {
    const ok = await submit({ rating: 'down', reason, comment: comment.trim() || null });
    if (ok) {
      setDetailsOpen(false);
      toast('Merci pour la précision');
    }
  };

  const isUp = rating === 'up';
  const isDown = rating === 'down';

  return (
    <TooltipProvider delayDuration={300}>
    <div className="flex items-center gap-1">
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleUp}
            aria-pressed={isUp}
            aria-label="Bonne réponse"
            className={cn(
              'h-6 w-6 p-0 rounded-md transition-colors hover:bg-grey-100',
              isUp ? 'text-grey-900 bg-grey-100 hover:text-grey-900' : 'text-grey-400 hover:text-grey-700',
            )}
          >
            <ThumbsUp className="h-3.5 w-3.5" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="text-xs">Bonne réponse</TooltipContent>
      </Tooltip>

      <Popover open={detailsOpen} onOpenChange={setDetailsOpen}>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverPrimitive.Anchor asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDown}
                aria-pressed={isDown}
                aria-label="Réponse à améliorer"
                className={cn(
                  'h-6 w-6 p-0 rounded-md transition-colors hover:bg-grey-100',
                  isDown ? 'text-grey-900 bg-grey-100 hover:text-grey-900' : 'text-grey-400 hover:text-grey-700',
                )}
              >
                <ThumbsDown className="h-3.5 w-3.5" />
              </Button>
            </PopoverPrimitive.Anchor>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="text-xs">Réponse à améliorer</TooltipContent>
        </Tooltip>

        <PopoverContent align="start" side="top" className="w-80">
          <p className="text-xs font-medium text-grey-800">C'est noté.</p>
          <p className="text-xs text-grey-500 mb-2.5">
            Une précision pour nous aider à corriger&nbsp;? <span className="text-grey-400">(facultatif)</span>
          </p>
          <div className="flex flex-wrap gap-1.5 mb-2.5">
            {MESSAGE_FEEDBACK_REASON_CATALOG.map((r) => (
              <button
                key={r.key}
                type="button"
                onClick={() => setReason((prev) => (prev === r.key ? null : r.key))}
                className={cn(
                  'px-2.5 py-1 rounded-full text-xs border transition-colors',
                  reason === r.key
                    ? 'border-grey-400 bg-grey-100 text-grey-900'
                    : 'border-grey-200 text-grey-600 hover:bg-grey-50',
                )}
              >
                {r.label}
              </button>
            ))}
          </div>
          {/* Border lives on the wrapper, NOT the textarea: a global style from
              HectorPromptInput forces `textarea:focus { border-color: transparent
              !important }` app-wide, which would otherwise strip the border on focus.
              Static `border-input` matches the site's Input/Textarea primitives —
              one constant border, no focus ring/colour change. */}
          <div className="rounded-md border border-input bg-background mb-2">
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value.slice(0, MESSAGE_FEEDBACK_COMMENT_MAX))}
              placeholder="Ajouter un détail…"
              rows={2}
              className="w-full bg-transparent p-2 text-xs resize-none border-0 outline-none"
            />
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setDetailsOpen(false)}
              className="h-7 text-xs text-grey-600 hover:text-grey-900 hover:bg-grey-100"
            >
              Fermer
            </Button>
            <Button size="sm" onClick={handleSendDetails} disabled={pending} className="h-7 text-xs">
              Envoyer
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
    </TooltipProvider>
  );
}
